import React, { useState, useEffect, useCallback, useRef } from 'react';

const API_URL = 'https://humble-ops-production.up.railway.app';
const API_KEY = 'humble-ops-secret-2024';
const VAPID_PUBLIC_KEY = 'BH5AkMLUWXgx2jzmH-XtiqEAHGQu8pSQVK5td7srqJPg7mErrwp05ZrUWIay-p7rFYtD-PkJQI9pc_1Tuis72oU';

const C = {
  bg0: '#0A0A0F', bg1: '#111118', bg2: '#16161F', bg3: '#1C1C28',
  accent: '#5B8DEF', accentDim: 'rgba(91,141,239,0.15)',
  accentBorder: 'rgba(91,141,239,0.3)',
  pos: '#34C759', posDim: 'rgba(52,199,89,0.12)',
  posBorder: 'rgba(52,199,89,0.35)',
  neg: '#FF3B30', negDim: 'rgba(255,59,48,0.1)',
  negBorder: 'rgba(255,59,48,0.3)',
  amb: '#FF9F0A', ambDim: 'rgba(255,159,10,0.12)',
  ambBorder: 'rgba(255,159,10,0.3)',
  t1: '#F0F0F8', t2: '#8888A0', t3: '#55556A',
  border: 'rgba(255,255,255,0.07)',
  borderHi: 'rgba(255,255,255,0.12)',
};

// Static fallback businesses — always shown
const STATIC_BUSINESSES = [
  { key: 'humble_ops', name: 'Humble Ops', icon: '🏢', agents: ['coo', 'software_engineer', 'legal_advisor', 'tax_advisor', 'portfolio_analyst', 'market_analyst'] },
  { key: 'humble_capital', name: 'Humble Capital', icon: '💼', agents: ['humble_capital_ceo', 'investment_analyst', 'trading_analyst', 'investment_strategist', 'brand_manager', 'hc_software_engineer'] },
  { key: 'humble_printing', name: 'Humble Printing', icon: '🖨️', agents: ['humble_printing_ceo', 'supply_chain_manager', 'operations_manager', 'order_manager', 'print_queue_specialist', 'marketing_manager', 'customer_support', 'hp_market_analyst', 'hp_software_engineer'] },
];

const AGENT_DISPLAY_NAMES: Record<string, string> = {
  coo: 'Chief Operating Officer',
  software_engineer: 'Software Engineer',
  legal_advisor: 'Legal Advisor',
  tax_advisor: 'Tax Advisor',
  portfolio_analyst: 'Portfolio Analyst',
  market_analyst: 'Market Analyst',
  humble_capital_ceo: 'Chief Executive Officer',
  investment_analyst: 'Investment Analyst',
  trading_analyst: 'Trading Analyst',
  investment_strategist: 'Investment Strategist',
  brand_manager: 'Brand Manager',
  hc_software_engineer: 'Software Engineer',
  humble_printing_ceo: 'Chief Executive Officer',
  supply_chain_manager: 'Supply Chain Manager',
  operations_manager: 'Operations Manager',
  order_manager: 'Order Manager',
  print_queue_specialist: 'Print Queue Specialist',
  marketing_manager: 'Marketing Manager',
  customer_support: 'Customer Support',
  hp_market_analyst: 'Market Analyst',
  hp_software_engineer: 'Software Engineer',
};

function displayName(key: string): string {
  return AGENT_DISPLAY_NAMES[key] || key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function bizName(key: string, businesses: any[]): string {
  const b = businesses.find(x => x.key === key);
  return b ? b.name : key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

async function apiFetch(path: string, method = 'GET', body?: object) {
  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', 'X-API-Key': API_KEY },
    body: body ? JSON.stringify(body) : undefined,
  });
  return res.json();
}

function fmt(n: number, d = 2) {
  return (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
}

const statusColors: Record<string, [string, string]> = {
  pending:          [C.ambDim,    C.amb],
  running:          [C.accentDim, C.accent],
  completed:        [C.posDim,    C.pos],
  failed:           [C.negDim,    C.neg],
  cancelled:        ['rgba(85,85,106,0.15)', C.t3],
  active:           [C.posDim,    C.pos],
  pending_response: [C.ambDim,    C.amb],
};

const logStatusIcon: Record<string, string> = { running: '⟳', done: '✓', failed: '✗' };
const logStatusColor: Record<string, string> = { running: C.accent, done: C.pos, failed: C.neg };

const STATUS_ORDER: Record<string, number> = { running: 0, pending_response: 1, pending: 2, failed: 3, cancelled: 4, completed: 5 };

function Badge({ status }: { status: string }) {
  const [bg, color] = statusColors[status] || [C.bg3, C.t3];
  const label = status === 'pending_response' ? 'needs input' : status;
  return <span style={{ background: bg, color, borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>{label}</span>;
}

function BizBadge({ bizKey, businesses }: { bizKey: string; businesses: any[] }) {
  return (
    <span style={{ background: C.bg3, color: C.t3, borderRadius: 6, padding: '2px 8px', fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap', border: `0.5px solid ${C.border}` }}>
      {bizName(bizKey, businesses)}
    </span>
  );
}

function Spinner() { return <div style={st.spinner} />; }

function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

async function registerPushNotifications() {
  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return;
    const reg = await navigator.serviceWorker.ready;
    const existing = await reg.pushManager.getSubscription();
    const sub = existing || await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) });
    await apiFetch('/register-push', 'POST', {
      endpoint: sub.endpoint,
      keys: {
        p256dh: btoa(Array.from(new Uint8Array(sub.getKey('p256dh')!)).map(b => String.fromCharCode(b)).join('')),
        auth: btoa(Array.from(new Uint8Array(sub.getKey('auth')!)).map(b => String.fromCharCode(b)).join(''))
      }
    });
  } catch(e) { console.log('[Push] Failed:', e); }
}

function fileToBase64(file: File): Promise<{ base64: string; mime_type: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve({ base64: (reader.result as string).split(',')[1], mime_type: file.type });
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function ChatInputBar({ onSend, placeholder, disabled }: {
  onSend: (text: string, image?: { base64: string; mime_type: string; preview: string }) => void;
  placeholder: string; disabled: boolean;
}) {
  const [input, setInput] = useState('');
  const [image, setImage] = useState<{ base64: string; mime_type: string; preview: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    const { base64, mime_type } = await fileToBase64(file);
    setImage({ base64, mime_type, preview: URL.createObjectURL(file) });
    e.target.value = '';
  };

  const handleSend = () => {
    if (!input.trim() && !image) return;
    onSend(input.trim(), image || undefined);
    setInput(''); setImage(null);
  };

  return (
    <div style={{ background: C.bg1, borderTop: `0.5px solid ${C.border}`, flexShrink: 0 }}>
      {image && (
        <div style={{ padding: '8px 12px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
          <img src={image.preview} alt="attachment" style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover' }} />
          <button onClick={() => setImage(null)} style={{ background: C.negDim, border: `0.5px solid ${C.negBorder}`, borderRadius: 6, padding: '3px 8px', color: C.neg, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}>Remove</button>
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, padding: 12, alignItems: 'flex-end' }}>
        <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFile} />
        <button onClick={() => fileRef.current?.click()} style={{ background: C.bg3, border: `0.5px solid ${C.border}`, borderRadius: 12, width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, fontSize: 18 }}>📷</button>
        <textarea style={st.chatInput} value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }}}
          placeholder={placeholder} rows={1} disabled={disabled} />
        <button style={{ ...st.sendBtn, opacity: disabled ? 0.4 : 1 }} onClick={handleSend} disabled={disabled}>Send</button>
      </div>
    </div>
  );
}

function Markdown({ text }: { text: string }) {
  const lines = text.split('\n'); const elements: React.ReactNode[] = []; let i = 0;
  const renderInline = (line: string): React.ReactNode => {
    const parts: React.ReactNode[] = []; let remaining = line; let key = 0;
    while (remaining.length > 0) {
      const codeMatch = remaining.match(/^(.*?)`([^`]+)`(.*)/s);
      const boldMatch = remaining.match(/^(.*?)\*\*([^*]+)\*\*(.*)/s);
      const codeIdx = codeMatch ? (codeMatch[1]?.length ?? 0) : Infinity;
      const boldIdx = boldMatch ? (boldMatch[1]?.length ?? 0) : Infinity;
      if (codeIdx === Infinity && boldIdx === Infinity) { parts.push(<span key={key++}>{remaining}</span>); break; }
      if (codeIdx <= boldIdx && codeMatch) {
        if (codeMatch[1]) parts.push(<span key={key++}>{codeMatch[1]}</span>);
        parts.push(<code key={key++} style={{ background: C.bg3, borderRadius: 4, padding: '1px 5px', fontSize: 12, fontFamily: 'monospace', color: C.accent }}>{codeMatch[2]}</code>);
        remaining = codeMatch[3];
      } else if (boldMatch) {
        if (boldMatch[1]) parts.push(<span key={key++}>{boldMatch[1]}</span>);
        parts.push(<strong key={key++} style={{ color: C.t1, fontWeight: 600 }}>{boldMatch[2]}</strong>);
        remaining = boldMatch[3];
      } else { parts.push(<span key={key++}>{remaining}</span>); break; }
    }
    return <>{parts}</>;
  };
  while (i < lines.length) {
    const line = lines[i];
    if (line.startsWith('```')) { const codeLines: string[] = []; i++; while (i < lines.length && !lines[i].startsWith('```')) { codeLines.push(lines[i]); i++; } elements.push(<pre key={i} style={{ background: C.bg3, borderRadius: 8, padding: '10px 12px', fontSize: 12, overflowX: 'auto', margin: '6px 0', fontFamily: 'monospace', color: C.t2, border: `0.5px solid ${C.border}` }}>{codeLines.join('\n')}</pre>); i++; continue; }
    if (line.startsWith('### ')) { elements.push(<div key={i} style={{ color: C.t1, fontWeight: 600, fontSize: 14, marginTop: 10, marginBottom: 4 }}>{renderInline(line.slice(4))}</div>); i++; continue; }
    if (line.startsWith('## ')) { elements.push(<div key={i} style={{ color: C.t1, fontWeight: 600, fontSize: 15, marginTop: 12, marginBottom: 4 }}>{renderInline(line.slice(3))}</div>); i++; continue; }
    if (line.startsWith('# ')) { elements.push(<div key={i} style={{ color: C.t1, fontWeight: 700, fontSize: 16, marginTop: 14, marginBottom: 6 }}>{renderInline(line.slice(2))}</div>); i++; continue; }
    if (line.startsWith('- ') || line.startsWith('* ')) { elements.push(<div key={i} style={{ display: 'flex', gap: 8, marginBottom: 3 }}><span style={{ color: C.accent, flexShrink: 0, marginTop: 1 }}>•</span><span style={{ color: C.t2, fontSize: 14, lineHeight: 1.6 }}>{renderInline(line.slice(2))}</span></div>); i++; continue; }
    const numMatch = line.match(/^(\d+)\. (.*)/);
    if (numMatch) { elements.push(<div key={i} style={{ display: 'flex', gap: 8, marginBottom: 3 }}><span style={{ color: C.accent, flexShrink: 0, minWidth: 16 }}>{numMatch[1]}.</span><span style={{ color: C.t2, fontSize: 14, lineHeight: 1.6 }}>{renderInline(numMatch[2])}</span></div>); i++; continue; }
    if (line.startsWith('---') || line.startsWith('___')) { elements.push(<div key={i} style={{ height: 0.5, background: C.border, margin: '10px 0' }} />); i++; continue; }
    if (line.trim() === '') { elements.push(<div key={i} style={{ height: 6 }} />); i++; continue; }
    elements.push(<div key={i} style={{ color: C.t2, fontSize: 14, lineHeight: 1.6, marginBottom: 2 }}>{renderInline(line)}</div>); i++;
  }
  return <div>{elements}</div>;
}

function ActivityFeed({ logs }: { logs: any[] }) {
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => { const c = containerRef.current; if (c) c.scrollTop = c.scrollHeight; }, [logs]);
  if (!logs.length) return <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Spinner /><span style={{ color: C.accent, fontSize: 12 }}>Starting up...</span></div>;
  return (
    <div ref={containerRef} style={{ background: C.bg1, borderRadius: 8, padding: '8px 10px', maxHeight: 160, overflowY: 'auto' }}>
      {logs.map((log, i) => {
        const icon = logStatusIcon[log.status] || '·';
        const color = logStatusColor[log.status] || C.t3;
        const isLast = i === logs.length - 1;
        return (
          <div key={log.id || i} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 4, opacity: isLast ? 1 : 0.65 }}>
            <span style={{ color, fontSize: 11, fontWeight: 700, flexShrink: 0, marginTop: 1, width: 12, textAlign: 'center' }}>
              {log.status === 'running' && isLast ? <Spinner /> : icon}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <span style={{ color, fontSize: 11, fontWeight: 600, marginRight: 6 }}>{log.tool_name}</span>
              <span style={{ color: C.t3, fontSize: 11, wordBreak: 'break-word' }}>{log.summary}</span>
            </div>
            <span style={{ color: C.t3, fontSize: 10, flexShrink: 0 }}>{new Date(log.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
          </div>
        );
      })}
    </div>
  );
}

function DashboardTab() {
  const [businesses, setBusinesses] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await apiFetch('/financials');
      setBusinesses((data.businesses || []).map((b: any) => {
        let notes: any = {};
        try { notes = JSON.parse(b.notes || '{}'); } catch {}
        return { key: b.business, name: notes.name || b.business === 'humble_capital' ? 'Humble Capital' : b.business === 'humble_printing' ? 'Humble Printing' : b.business.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()),
          total: b.cash_balance || 0, dayPnl: notes.day_pnl || 0, coinbase: notes.coinbase || 0,
          webull: notes.webull || 0, futures: notes.futures || 0, apexActive: b.business === 'humble_capital',
          totalOrders: notes.total_orders || 0, pendingOrders: notes.pending_orders || 0,
          idlePrinters: notes.idle_printers || 0, totalPrinters: notes.total_printers || 0, lastSync: notes.last_sync || '' };
      }));
    } catch(e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);
  if (loading) return <div style={st.center}><Spinner /></div>;

  return (
    <div style={st.scroll}>
      {businesses.map(b => (
        <div key={b.key} style={{ ...st.card, cursor: 'pointer' }} onClick={() => setExpanded(expanded === b.key ? null : b.key)}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ color: C.t2, fontSize: 12, marginBottom: 4 }}>{b.name}</div>
              <div style={{ color: C.t1, fontSize: 28, fontWeight: 600, letterSpacing: -1 }}>${fmt(b.total)}</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
              <span style={{ background: b.dayPnl >= 0 ? C.posDim : C.negDim, color: b.dayPnl >= 0 ? C.pos : C.neg, borderRadius: 10, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>{b.dayPnl >= 0 ? '+' : ''}${fmt(Math.abs(b.dayPnl))} today</span>
              {b.apexActive && <span style={{ background: C.accentDim, color: C.accent, border: `0.5px solid ${C.accentBorder}`, borderRadius: 10, padding: '2px 8px', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 6, height: 6, borderRadius: 3, background: C.pos, display: 'inline-block' }} />APEX active</span>}
            </div>
          </div>
          {b.coinbase > 0 && <div style={st.metricsRow}>{[['$'+fmt(b.coinbase,0),'Coinbase'],['$'+fmt(b.webull,0),'Webull'],['$'+fmt(b.futures,0),'Futures']].map(([v,l]) => (<div key={l} style={st.metric}><div style={st.metricV}>{v}</div><div style={st.metricL}>{l}</div></div>))}</div>}
          {b.totalPrinters > 0 && <div style={st.metricsRow}>{[[`${b.idlePrinters}/${b.totalPrinters}`,'Printers'],[String(b.totalOrders),'Orders'],[String(b.pendingOrders),'Pending']].map(([v,l]) => (<div key={l} style={st.metric}><div style={st.metricV}>{v}</div><div style={st.metricL}>{l}</div></div>))}</div>}
          {expanded === b.key && b.lastSync && <div style={{ marginTop: 12, paddingTop: 10, borderTop: `0.5px solid ${C.border}`, color: C.t3, fontSize: 12 }}>Last sync: {new Date(b.lastSync).toLocaleTimeString()}</div>}
        </div>
      ))}
      <div style={{ ...st.card, border: `0.5px dashed ${C.borderHi}`, background: C.bg1, textAlign: 'center', color: C.t3, cursor: 'pointer', padding: 16 }}>+ Propose a new business</div>
    </div>
  );
}

function ProposalsTab() {
  const [proposals, setProposals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [generatingPdf, setGeneratingPdf] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [m, a] = await Promise.all([apiFetch('/proposals'), apiFetch('/apex-proposals')]);
      const market = (m.proposals || []).map((p: any) => {
        let presentation = '';
        try {
          const raw = typeof p.presentation_json === 'string' ? JSON.parse(p.presentation_json) : p.presentation_json;
          presentation = raw?.presentation || raw?.summary || '';
        } catch {}
        return { id: p.id || String(Math.random()), type: 'market', badge: 'Market Analyst', title: p.title || 'New proposal', detail: p.summary || '', cost: p.startup_cost, revenue: p.monthly_revenue_estimate, status: p.status || 'pending', agentRunnable: p.agent_runnable, presentation };
      });
      const apex = (a.proposals || []).map((p: any) => ({ id: p.id || String(Math.random()), type: 'apex', badge: `APEX ${p.proposal_type || 'TRADE'}`, title: p.symbol || 'Trade', detail: p.reason || '', status: p.status || 'pending' }));
      setProposals([...apex, ...market].filter(p => p.status === 'pending'));
    } catch(e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (p: any, action: string) => {
    setActing(`${p.id}-${action}`);
    try {
      await apiFetch(p.type === 'apex' ? `/apex-proposals/${p.id}/${action}` : `/proposals/${p.id}/${action}`, 'POST');
      setProposals(prev => prev.filter(x => x.id !== p.id));
    } catch(e) { console.error(e); }
    setActing(null);
  };

  const generatePdf = async (p: any) => {
    setGeneratingPdf(p.id);
    try {
      await apiFetch('/tasks', 'POST', {
        business: 'humble_ops', agent: 'market_analyst',
        title: `Generate proposal PDF: ${p.title}`,
        description: (
          `Generate a professional PDF business proposal for: ${p.title}\n\n` +
          `Summary: ${p.detail}\nStartup Cost: $${p.cost}\nEst. Monthly Revenue: $${p.revenue}/mo\n\n` +
          `Full details:\n${p.presentation}\n\n` +
          `Use generate_pdf_report with sections: 1. Executive Summary 2. Opportunity Overview ` +
          `3. Financial Projections (table with cost breakdown) 4. Agent Execution Plan 5. Risk Analysis 6. Recommendation\n` +
          `Name the file: proposal_${(p.title || '').toLowerCase().replace(/[^a-z0-9]/g, '_').slice(0, 30)}.pdf\n` +
          `Confirm the PDF was generated and report the filename.`
        ),
        priority: 2
      });
      alert('PDF generation queued! Check Tasks tab then Docs tab when complete.');
    } catch(e) { console.error(e); }
    setGeneratingPdf(null);
  };

  if (loading) return <div style={st.center}><Spinner /></div>;
  if (proposals.length === 0) return <div style={st.center}><div style={{ color: C.t1, fontSize: 16, marginBottom: 8 }}>No pending proposals</div><div style={{ color: C.t3, fontSize: 13 }}>APEX and Market Analyst proposals appear here</div></div>;

  return (
    <div style={st.scroll}>
      {proposals.map(p => {
        const isExpanded = expandedId === p.id;
        return (
          <div key={p.id} style={st.card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
              <span style={{ background: p.type === 'apex' ? C.ambDim : C.accentDim, color: p.type === 'apex' ? C.amb : C.accent, borderRadius: 8, padding: '3px 10px', fontSize: 11, fontWeight: 600 }}>{p.badge}</span>
              {p.agentRunnable && <span style={{ background: C.posDim, color: C.pos, borderRadius: 8, padding: '3px 8px', fontSize: 11, fontWeight: 600 }}>🤖 Agent runnable</span>}
            </div>
            <div style={{ color: C.t1, fontSize: 18, fontWeight: 600, marginBottom: 8 }}>{p.title}</div>
            <div style={{ color: C.t2, fontSize: 13, lineHeight: 1.6, marginBottom: 12 }}>{p.detail}</div>
            {p.cost != null && (
              <div style={{ display: 'flex', marginBottom: 14, borderRadius: 10, overflow: 'hidden', background: C.bg1 }}>
                <div style={{ flex: 1, padding: '10px 0', textAlign: 'center' }}><div style={{ color: C.t1, fontSize: 16, fontWeight: 600 }}>${fmt(p.cost, 0)}</div><div style={{ color: C.t3, fontSize: 11, marginTop: 2 }}>Startup cost</div></div>
                <div style={{ width: 0.5, background: C.border }} />
                <div style={{ flex: 1, padding: '10px 0', textAlign: 'center' }}><div style={{ color: C.pos, fontSize: 16, fontWeight: 600 }}>${fmt(p.revenue || 0, 0)}/mo</div><div style={{ color: C.t3, fontSize: 11, marginTop: 2 }}>Est. revenue</div></div>
                <div style={{ width: 0.5, background: C.border }} />
                <div style={{ flex: 1, padding: '10px 0', textAlign: 'center' }}><div style={{ color: C.accent, fontSize: 16, fontWeight: 600 }}>{p.cost > 0 ? `${Math.round((p.revenue || 0) / p.cost * 100)}%` : '—'}</div><div style={{ color: C.t3, fontSize: 11, marginTop: 2 }}>Monthly ROI</div></div>
              </div>
            )}
            {p.presentation && (
              <button onClick={() => setExpandedId(isExpanded ? null : p.id)}
                style={{ background: 'none', border: `0.5px solid ${C.border}`, borderRadius: 8, padding: '7px 14px', color: C.t2, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 10, width: '100%', textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>📋 Full Business Breakdown</span>
                <span style={{ color: C.accent }}>{isExpanded ? '▲ Hide' : '▼ Show'}</span>
              </button>
            )}
            {isExpanded && p.presentation && (
              <div style={{ background: C.bg1, borderRadius: 10, padding: 14, marginBottom: 12, border: `0.5px solid ${C.border}` }}>
                <Markdown text={p.presentation} />
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button style={st.btnApprove} disabled={acting !== null} onClick={() => act(p, 'approve')}>{acting === `${p.id}-approve` ? '...' : '✓ Approve & Launch'}</button>
              <button style={st.btnReject} disabled={acting !== null} onClick={() => act(p, 'reject')}>{acting === `${p.id}-reject` ? '...' : 'Reject'}</button>
              {p.presentation && <button onClick={() => generatePdf(p)} disabled={generatingPdf === p.id} style={{ background: C.bg3, border: `0.5px solid ${C.border}`, borderRadius: 9, padding: '10px 14px', color: C.t2, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>{generatingPdf === p.id ? '...' : '📄 Generate PDF'}</button>}
            </div>
            {p.agentRunnable && <div style={{ marginTop: 8, color: C.t3, fontSize: 11 }}>✨ Approving will automatically bootstrap this business — CEO creation, setup tasks, and daily briefings start immediately.</div>}
          </div>
        );
      })}
    </div>
  );
}

function ApprovalsTab() {
  const [approvals, setApprovals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [responses, setResponses] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [expandedContext, setExpandedContext] = useState<Set<string>>(new Set());
  const CONTEXT_PREVIEW = 200;

  const load = useCallback(async () => {
    try { const data = await apiFetch('/approvals'); setApprovals(data.approvals || []); } catch(e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const respond = async (approvalId: string) => {
    const response = responses[approvalId]; if (!response?.trim()) return;
    setSubmitting(approvalId);
    try {
      await apiFetch(`/approvals/${approvalId}/respond`, 'POST', { response });
      setApprovals(prev => prev.filter(a => a.id !== approvalId));
      setResponses(prev => { const n = {...prev}; delete n[approvalId]; return n; });
    } catch(e) { console.error(e); }
    setSubmitting(null);
  };

  const dismiss = async (approvalId: string) => {
    try { await apiFetch(`/approvals/${approvalId}/dismiss`, 'POST'); setApprovals(prev => prev.filter(a => a.id !== approvalId)); } catch(e) { console.error(e); }
  };

  const clearAll = async () => {
    try { await apiFetch('/approvals/clear', 'DELETE'); setApprovals([]); } catch(e) { console.error(e); }
  };

  if (loading) return <div style={st.center}><Spinner /></div>;
  if (approvals.length === 0) return (
    <div style={st.center}>
      <div style={{ fontSize: 32, marginBottom: 16 }}>✓</div>
      <div style={{ color: C.t1, fontSize: 16, marginBottom: 8 }}>No pending approvals</div>
      <div style={{ color: C.t3, fontSize: 13, textAlign: 'center' }}>When agents need your input they will appear here</div>
    </div>
  );

  return (
    <div style={st.scroll}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
        <button onClick={clearAll} style={{ background: C.negDim, border: `0.5px solid ${C.negBorder}`, borderRadius: 8, padding: '6px 14px', color: C.neg, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Clear All</button>
      </div>
      {approvals.map(a => {
        const context = a.context || '';
        const isExpanded = expandedContext.has(a.id);
        const showToggle = context.length > CONTEXT_PREVIEW;
        const displayContext = isExpanded ? context : context.slice(0, CONTEXT_PREVIEW);
        return (
          <div key={a.id} style={st.card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 16, background: C.ambDim, border: `0.5px solid ${C.ambBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.amb, fontSize: 14, fontWeight: 700, flexShrink: 0 }}>{a.agent_name?.[0]?.toUpperCase() || '?'}</div>
              <div>
                <div style={{ color: C.t1, fontSize: 13, fontWeight: 600 }}>{displayName(a.agent_name || '')}</div>
                <div style={{ color: C.t3, fontSize: 11 }}>{a.business?.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())} · {new Date(a.created_at).toLocaleTimeString()}</div>
              </div>
              <span style={{ marginLeft: 'auto', background: C.ambDim, color: C.amb, borderRadius: 8, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>Needs input</span>
            </div>
            <div style={{ color: C.t1, fontSize: 15, fontWeight: 600, marginBottom: 8 }}>{a.question}</div>
            {context && (
              <div style={{ background: C.bg1, borderRadius: 8, padding: 10, marginBottom: 12 }}>
                <Markdown text={displayContext + (!isExpanded && showToggle ? '...' : '')} />
                {showToggle && <button onClick={() => setExpandedContext(prev => { const next = new Set(prev); next.has(a.id) ? next.delete(a.id) : next.add(a.id); return next; })} style={{ background: 'none', border: 'none', color: C.accent, fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: '6px 0 0', fontFamily: 'inherit' }}>{isExpanded ? '▲ Show less' : '▼ Show more'}</button>}
              </div>
            )}
            <textarea style={{ width: '100%', background: C.bg3, border: `0.5px solid ${C.border}`, borderRadius: 10, padding: '10px 12px', color: C.t1, fontSize: 14, fontFamily: 'inherit', outline: 'none', resize: 'vertical', minHeight: 60, marginBottom: 10, boxSizing: 'border-box' } as React.CSSProperties}
              value={responses[a.id] || ''} onChange={e => setResponses(prev => ({ ...prev, [a.id]: e.target.value }))} placeholder="Type your response..." />
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={{ ...st.btnApprove, flex: 1 }} disabled={submitting === a.id || !responses[a.id]?.trim()} onClick={() => respond(a.id)}>{submitting === a.id ? '...' : 'Send Response'}</button>
              <button onClick={() => dismiss(a.id)} style={{ background: C.bg3, border: `0.5px solid ${C.border}`, borderRadius: 9, padding: '10px 14px', color: C.t3, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Dismiss</button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Task Card Component ───────────────────────────────────────
function TaskCard({ task, businesses, onRun, onCancel, onDelete, onRerun, running, copiedId, setCopiedId, taskLogs, loadLogsForTask, selectedBiz }: any) {
  const [minimized, setMinimized] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [followUpOpen, setFollowUpOpen] = useState(false);
  const [followUpText, setFollowUpText] = useState('');
  const [approvalResponse, setApprovalResponse] = useState('');
  const [submittingApproval, setSubmittingApproval] = useState(false);
  const RESULT_PREVIEW = 200;
  const result = task.result || '';
  const showToggle = result.length > RESULT_PREVIEW;
  const displayResult = expanded ? result : result.slice(0, RESULT_PREVIEW);
  const logs = taskLogs[task.id] || [];
  const isCancelledOrDone = ['completed', 'failed', 'cancelled'].includes(task.status);

  return (
    <div style={{ ...st.card, opacity: task.status === 'cancelled' ? 0.7 : 1 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: minimized ? 0 : 8 }}>
        <div style={{ flex: 1, marginRight: 8 }}>
          <div style={{ color: C.t1, fontSize: 15, fontWeight: 600, marginBottom: 4 }}>{task.title}</div>
          {!selectedBiz && <BizBadge bizKey={task.business} businesses={businesses} />}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          <Badge status={task.pending_approval ? 'pending_response' : task.status} />
          <button onClick={() => setMinimized(!minimized)} style={{ background: 'none', border: 'none', color: C.t3, fontSize: 12, cursor: 'pointer', padding: '0 2px', fontFamily: 'inherit', lineHeight: 1 }}>{minimized ? '▼' : '▲'}</button>
        </div>
      </div>
      {minimized && task.agent && <div style={{ color: C.t3, fontSize: 11, marginTop: 2 }}>→ {displayName(task.agent)} · {new Date(task.created_at).toLocaleTimeString()}</div>}
      {!minimized && (
        <>
          {task.agent && <div style={{ color: C.accent, fontSize: 12, marginBottom: 6 }}>→ {displayName(task.agent)}</div>}
          {task.description && <div style={{ color: C.t2, fontSize: 13, marginBottom: 8, lineHeight: 1.6 }}>{task.description}</div>}
          {(task.status === 'running' || logs.length > 0) && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ color: C.t3, fontSize: 11, marginBottom: 4 }}>Activity{task.status !== 'running' ? ` (${task.status})` : ''}:</div>
              <ActivityFeed logs={logs} />
            </div>
          )}
          {result && (
            <div style={{ background: C.bg1, borderRadius: 8, padding: 10, marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <div style={{ color: C.t3, fontSize: 11 }}>Result:</div>
                <button onClick={() => { navigator.clipboard.writeText(result); setCopiedId(task.id); setTimeout(() => setCopiedId(null), 2000); }} style={{ background: C.bg3, border: 'none', borderRadius: 6, padding: '3px 8px', color: C.t3, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}>{copiedId === task.id ? 'Copied!' : 'Copy'}</button>
              </div>
              <Markdown text={displayResult + (!expanded && showToggle ? '...' : '')} />
              {showToggle && <button onClick={() => setExpanded(!expanded)} style={{ background: 'none', border: 'none', color: C.accent, fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: '6px 0 0', fontFamily: 'inherit' }}>{expanded ? '▲ Show less' : '▼ Show more'}</button>}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
            {task.status === 'pending' && <button style={st.btnRun} disabled={running === task.id} onClick={() => onRun(task.id)}>{running === task.id ? '...' : '▶ Run'}</button>}
            {task.status === 'running' && <button style={st.btnCancel} onClick={() => onCancel(task.id)}>&times; Cancel</button>}
            {isCancelledOrDone && <button style={st.btnRun} onClick={() => onRerun(task)}>↺ Re-run</button>}
            {task.status === 'completed' && <button style={{ ...st.btnRun, background: followUpOpen ? C.accentDim : C.bg3, borderColor: followUpOpen ? C.accentBorder : C.border, color: followUpOpen ? C.accent : C.t2 }} onClick={() => { setFollowUpOpen(!followUpOpen); setFollowUpText(''); }}>↩ Follow up</button>}
            <button style={st.btnDel} onClick={() => onDelete(task.id)}>Delete</button>
          </div>
          {followUpOpen && (
            <div style={{ marginTop: 10, borderTop: `0.5px solid ${C.border}`, paddingTop: 10 }}>
              <textarea style={{ width: '100%', background: C.bg3, border: `0.5px solid ${C.accentBorder}`, borderRadius: 10, padding: '10px 12px', color: C.t1, fontSize: 13, fontFamily: 'inherit', outline: 'none', resize: 'vertical', minHeight: 60, boxSizing: 'border-box' } as React.CSSProperties}
                value={followUpText} onChange={e => setFollowUpText(e.target.value)} placeholder="Type your follow-up instruction..." autoFocus />
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button style={{ ...st.btnRun, flex: 1 }} onClick={async () => {
                  if (!followUpText.trim()) return;
                  const desc = followUpText + '\n\nPrevious task result:\n' + (task.result || '');
                  const data = await apiFetch('/tasks', 'POST', { business: task.business || selectedBiz, title: '↩ ' + task.title, description: desc, agent: task.agent });
                  if (data.task?.id) await apiFetch(`/tasks/${data.task.id}/run`, 'POST');
                  setFollowUpOpen(false); setFollowUpText('');
                }}>Send</button>
                <button style={st.btnDel} onClick={() => { setFollowUpOpen(false); setFollowUpText(''); }}>Cancel</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function TasksTab() {
  const [businesses, setBusinesses] = useState<any[]>([]);
  const [selectedBiz, setSelectedBiz] = useState<string | null>(null);
  const [tasks, setTasks] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newAgent, setNewAgent] = useState('');
  const [running, setRunning] = useState<string | null>(null);
  const [autoRunStatus, setAutoRunStatus] = useState<any>({ enabled: false, queued_tasks: 0, paused_for_approval: false });
  const [togglingAuto, setTogglingAuto] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [taskLogs, setTaskLogs] = useState<Record<string, any[]>>({});
  const pollRef = useRef<any>(null);
  const autoStatusRef = useRef<any>(null);

  // Load dynamic businesses
  const loadBusinesses = useCallback(async () => {
    try {
      const data = await apiFetch('/businesses');
      const dynamic = (data.businesses || []);
      // Merge static agents info with dynamic list
      const merged = dynamic.map((b: any) => {
        const staticB = STATIC_BUSINESSES.find(s => s.key === b.key);
        return { ...b, icon: staticB?.icon || '🏢', agents: staticB?.agents || [`${b.key}_ceo`] };
      });
      // Add any static businesses not in dynamic list
      STATIC_BUSINESSES.forEach(s => {
        if (!merged.find((m: any) => m.key === s.key)) merged.unshift(s);
      });
      setBusinesses(merged);
    } catch {
      setBusinesses(STATIC_BUSINESSES);
    }
  }, []);

  useEffect(() => { loadBusinesses(); }, [loadBusinesses]);

  const loadTasks = useCallback(async (bizKey: string | null) => {
    try {
      const url = bizKey && bizKey !== 'all' ? `/tasks?business=${bizKey}` : '/tasks';
      const data = await apiFetch(url);
      const sorted = (data.tasks || []).sort((a: any, b: any) => {
        const sa = STATUS_ORDER[a.status] ?? 10;
        const sb = STATUS_ORDER[b.status] ?? 10;
        if (sa !== sb) return sa - sb;
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
      setTasks(sorted); return sorted;
    } catch(e) { console.error(e); return []; }
  }, []);

  const loadAutoStatus = useCallback(async () => {
    try { const data = await apiFetch('/tasks/autorun/status'); setAutoRunStatus(data); } catch {}
  }, []);

  const loadLogsForTask = useCallback(async (taskId: string) => {
    try {
      const data = await apiFetch(`/tasks/${taskId}/logs`);
      setTaskLogs(prev => ({ ...prev, [taskId]: data.logs || [] }));
    } catch {}
  }, []);

  const loadLogsForRunningTasks = useCallback(async (currentTasks: any[]) => {
    for (const task of currentTasks.filter(t => t.status === 'running')) {
      await loadLogsForTask(task.id);
    }
  }, [loadLogsForTask]);

  useEffect(() => {
    if (selectedBiz === null) return;
    const hasRunning = tasks.some(t => t.status === 'running');
    if (hasRunning) {
      if (!pollRef.current) pollRef.current = setInterval(async () => {
        const updated = await loadTasks(selectedBiz);
        await loadLogsForRunningTasks(updated);
      }, 4000);
    } else {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [tasks, selectedBiz, loadTasks, loadLogsForRunningTasks]);

  useEffect(() => {
    loadAutoStatus();
    autoStatusRef.current = setInterval(loadAutoStatus, 8000);
    return () => { if (autoStatusRef.current) clearInterval(autoStatusRef.current); };
  }, [loadAutoStatus]);

  const selectBiz = (key: string) => {
    setSelectedBiz(key); setLoading(true);
    loadTasks(key).then(t => { setLoading(false); loadLogsForRunningTasks(t); });
  };

  const addTask = async () => {
    if (!newTitle.trim() || !selectedBiz || selectedBiz === 'all') return;
    await apiFetch('/tasks', 'POST', { business: selectedBiz, title: newTitle, description: newDesc, agent: newAgent });
    setNewTitle(''); setNewDesc(''); setNewAgent(''); setShowAdd(false);
    loadTasks(selectedBiz);
  };

  const runTask = async (taskId: string) => {
    setRunning(taskId);
    setTaskLogs(prev => ({ ...prev, [taskId]: [] }));
    try {
      const res = await fetch(`${API_URL}/tasks/${taskId}/run`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-API-Key': API_KEY } });
      if (!res.ok) { const err = await res.json(); alert(err.detail || 'Failed to run task.'); setRunning(null); return; }
      setTimeout(async () => {
        const updated = await loadTasks(selectedBiz);
        await loadLogsForRunningTasks(updated);
        await loadLogsForTask(taskId);
      }, 1500);
    } catch(e) { console.error(e); }
    setRunning(null);
  };

  const cancelTask = async (taskId: string) => {
    await apiFetch(`/tasks/${taskId}/cancel`, 'POST');
    loadTasks(selectedBiz);
  };

  const deleteTask = async (taskId: string) => {
    await apiFetch(`/tasks/${taskId}`, 'DELETE');
    setTasks(prev => prev.filter(t => t.id !== taskId));
  };

  const rerunTask = async (task: any) => {
    try {
      const data = await apiFetch('/tasks', 'POST', { business: task.business || selectedBiz, title: task.title, description: task.description, agent: task.agent });
      if (data.task?.id) { await apiFetch(`/tasks/${data.task.id}/run`, 'POST'); setTimeout(() => loadTasks(selectedBiz), 1500); }
    } catch(e) { console.error(e); }
  };

  const toggleAutoRun = async () => {
    setTogglingAuto(true);
    try {
      await apiFetch(autoRunStatus.enabled ? '/tasks/autorun/stop' : '/tasks/autorun/start', 'POST');
      await loadAutoStatus();
      if (selectedBiz) loadTasks(selectedBiz);
    } catch(e) { console.error(e); }
    setTogglingAuto(false);
  };

  const biz = businesses.find(b => b.key === selectedBiz);

  // Business selector screen
  if (!selectedBiz) return (
    <div style={st.scroll}>
      {/* Global "All Tasks" view */}
      <div style={{ ...st.card, cursor: 'pointer', background: C.bg1, border: `0.5px solid ${C.accentBorder}` }} onClick={() => selectBiz('all')}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: 28 }}>📋</div>
          <div>
            <div style={{ color: C.accent, fontSize: 16, fontWeight: 600 }}>All Tasks</div>
            <div style={{ color: C.t3, fontSize: 12, marginTop: 2 }}>View running & queued tasks across all businesses</div>
          </div>
        </div>
      </div>
      <div style={st.sectionTitle}>By Business</div>
      {businesses.map(b => (
        <div key={b.key} style={{ ...st.card, cursor: 'pointer' }} onClick={() => selectBiz(b.key)}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>{b.icon || '🏢'}</div>
          <div style={{ color: C.t1, fontSize: 16, fontWeight: 600 }}>{b.name}</div>
          <div style={{ color: C.t3, fontSize: 12, marginTop: 4 }}>{b.agents?.length || 1} team members</div>
        </div>
      ))}
    </div>
  );

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div style={st.subHeader}>
        <button style={st.backBtn} onClick={() => { setSelectedBiz(null); setTasks([]); }}>&larr; Back</button>
        <span style={st.subTitle}>{selectedBiz === 'all' ? 'All Tasks' : `${biz?.name || selectedBiz} Tasks`}</span>
        {selectedBiz !== 'all' && <button style={st.addBtn} onClick={() => setShowAdd(true)}>+ Add</button>}
        {selectedBiz === 'all' && <div style={{ width: 60 }} />}
      </div>

      {/* Auto-run bar */}
      <div style={{ padding: '10px 16px', background: C.bg1, borderBottom: `0.5px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={toggleAutoRun} disabled={togglingAuto} style={{ background: autoRunStatus.enabled ? C.posDim : C.bg3, border: `0.5px solid ${autoRunStatus.enabled ? C.posBorder : C.border}`, borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontFamily: 'inherit', color: autoRunStatus.enabled ? C.pos : C.t2, fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
            {togglingAuto ? <Spinner /> : (autoRunStatus.enabled ? '⏸ Stop Auto' : '▶ Auto Run')}
          </button>
          {autoRunStatus.enabled && (
            autoRunStatus.paused_for_approval
              ? <span style={{ color: C.amb, fontSize: 12 }}>⏸ Paused — {autoRunStatus.pending_approvals} approval{autoRunStatus.pending_approvals !== 1 ? 's' : ''} waiting</span>
              : autoRunStatus.running_task
                ? <span style={{ color: C.accent, fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}><Spinner /> Running...</span>
                : <span style={{ color: C.t3, fontSize: 12 }}>{autoRunStatus.queued_tasks} task{autoRunStatus.queued_tasks !== 1 ? 's' : ''} queued</span>
          )}
        </div>
        <button onClick={() => loadTasks(selectedBiz)} style={{ background: 'none', border: 'none', color: C.t3, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>↻ Refresh</button>
      </div>

      {loading ? <div style={st.center}><Spinner /></div> : (
        <div style={st.scroll}>
          {tasks.length === 0 && <div style={{ color: C.t3, fontSize: 13, textAlign: 'center', paddingTop: 40 }}>No tasks yet.{selectedBiz !== 'all' && ' Tap + Add to create one.'}</div>}
          {tasks.map(task => (
            <TaskCard
              key={task.id}
              task={task}
              businesses={businesses}
              selectedBiz={selectedBiz !== 'all' ? selectedBiz : null}
              onRun={runTask}
              onCancel={cancelTask}
              onDelete={deleteTask}
              onRerun={rerunTask}
              running={running}
              copiedId={copiedId}
              setCopiedId={setCopiedId}
              taskLogs={taskLogs}
              loadLogsForTask={loadLogsForTask}
            />
          ))}
        </div>
      )}

      {showAdd && selectedBiz !== 'all' && (
        <div style={st.modalOverlay} onClick={() => setShowAdd(false)}>
          <div style={st.modal} onClick={e => e.stopPropagation()}>
            <div style={st.modalTitle}>New Task</div>
            <div style={st.label}>TITLE *</div>
            <input style={st.input} value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="Task title" />
            <div style={st.label}>ASSIGN TO TEAM MEMBER</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
              {(biz?.agents || []).map((a: string) => (
                <button key={a} onClick={() => setNewAgent(newAgent === a ? '' : a)} style={{ background: newAgent === a ? C.accent : C.bg3, color: newAgent === a ? '#fff' : C.t2, border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: 12, cursor: 'pointer' }}>{displayName(a)}</button>
              ))}
            </div>
            <div style={st.label}>DESCRIPTION</div>
            <textarea style={{ ...st.input, height: 80, resize: 'vertical' } as React.CSSProperties} value={newDesc} onChange={e => setNewDesc(e.target.value)} placeholder="Additional details..." />
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button style={{ ...st.btnApprove, flex: 1 }} onClick={addTask}>Add Task</button>
              <button style={{ ...st.btnReject, flex: 1 }} onClick={() => { setShowAdd(false); setNewTitle(''); setNewDesc(''); setNewAgent(''); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AgentsTab() {
  const [selectedBiz, setSelectedBiz] = useState<string | null>(null);
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null);
  const [sessions, setSessions] = useState<any[]>([]);
  const [activeSession, setActiveSession] = useState<any | null>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [businesses, setBusinesses] = useState(STATIC_BUSINESSES);
  const endRef = useRef<HTMLDivElement>(null);
  const syncRef = useRef<any>(null);

  useEffect(() => {
    apiFetch('/businesses').then(data => {
      const dynamic = (data.businesses || []);
      const merged = dynamic.map((b: any) => {
        const s = STATIC_BUSINESSES.find(x => x.key === b.key);
        return { ...b, icon: s?.icon || '🏢', agents: s?.agents || [`${b.key}_ceo`] };
      });
      STATIC_BUSINESSES.forEach(s => { if (!merged.find((m: any) => m.key === s.key)) merged.unshift(s); });
      setBusinesses(merged);
    }).catch(() => {});
  }, []);

  const biz = businesses.find(b => b.key === selectedBiz);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  useEffect(() => {
    if (!activeSession) { if (syncRef.current) { clearInterval(syncRef.current); syncRef.current = null; } return; }
    syncRef.current = setInterval(async () => {
      try { const data = await apiFetch(`/sessions/${activeSession.id}`); const newMsgs = data.session?.messages || []; if (newMsgs.length !== messages.length) setMessages(newMsgs); } catch {}
    }, 3000);
    return () => { if (syncRef.current) { clearInterval(syncRef.current); syncRef.current = null; } };
  }, [activeSession, messages.length]);

  const loadSessions = async (bizKey: string, agent: string) => {
    try { const data = await apiFetch(`/sessions?business=${bizKey}&agent=${agent}`); setSessions((data.sessions || []).filter((s: any) => s.messages && s.messages.length > 0)); } catch(e) { console.error(e); }
  };

  const newSession = async () => {
    if (!selectedBiz || !selectedAgent) return;
    setLoading(true);
    try {
      const data = await apiFetch('/sessions', 'POST', { business: selectedBiz, agent_name: selectedAgent, session_title: `${displayName(selectedAgent)} ${new Date().toLocaleDateString()}` });
      const session = Array.isArray(data.session) ? data.session[0] : data.session;
      if (!session || !session.id) { setLoading(false); return; }
      setActiveSession(session); setMessages([]);
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  const sendMessage = async (text: string, image?: { base64: string; mime_type: string; preview: string }) => {
    if (!text && !image) return; if (!activeSession || sending) return;
    setSending(true);
    const userMsg: any = { role: 'user', content: text, ts: new Date().toISOString() };
    if (image) userMsg.image_preview = image.preview;
    setMessages(prev => [...prev, userMsg]);
    try {
      if (image) {
        const data = await apiFetch('/chat-with-image', 'POST', { text: text || 'What do you see in this image?', image_base64: image.base64, mime_type: image.mime_type });
        setMessages(prev => [...prev, { role: 'agent', content: data.response, ts: new Date().toISOString() }]);
      } else {
        const data = await apiFetch(`/sessions/${activeSession.id}/message`, 'POST', { text });
        setMessages(data.messages || []);
      }
    } catch { setMessages(prev => [...prev, { role: 'agent', content: 'Error sending message.', ts: new Date().toISOString() }]); }
    setSending(false);
  };

  if (activeSession) return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div style={st.subHeader}>
        <button style={st.backBtn} onClick={() => { setActiveSession(null); if (selectedBiz && selectedAgent) loadSessions(selectedBiz, selectedAgent); }}>&larr; Back</button>
        <span style={{ ...st.subTitle, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{activeSession.session_title || displayName(selectedAgent || '')}</span>
        <div style={{ width: 60 }} />
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {messages.map((msg, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
            <div style={{ background: msg.role === 'user' ? C.accent : C.bg2, color: C.t1, borderRadius: 16, borderBottomRightRadius: msg.role === 'user' ? 4 : 16, borderBottomLeftRadius: msg.role === 'user' ? 16 : 4, padding: '10px 14px', maxWidth: '82%', border: msg.role === 'agent' ? `0.5px solid ${C.border}` : 'none' }}>
              {msg.image_preview && <img src={msg.image_preview} alt="attachment" style={{ maxWidth: '100%', borderRadius: 8, marginBottom: 6 }} />}
              {msg.role === 'agent' ? <Markdown text={msg.content} /> : <div style={{ fontSize: 14, lineHeight: 1.6 }}>{msg.content}</div>}
              <div style={{ color: msg.role === 'user' ? 'rgba(255,255,255,0.5)' : C.t3, fontSize: 10, marginTop: 4 }}>{msg.ts ? new Date(msg.ts).toLocaleTimeString() : ''}</div>
            </div>
          </div>
        ))}
        {sending && <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: C.t3, fontSize: 12 }}><Spinner />{displayName(selectedAgent || '')} thinking...</div>}
        <div ref={endRef} />
      </div>
      <ChatInputBar onSend={sendMessage} placeholder={`Message ${displayName(selectedAgent || '')}...`} disabled={sending} />
    </div>
  );

  if (selectedAgent) return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div style={st.subHeader}>
        <button style={st.backBtn} onClick={() => setSelectedAgent(null)}>&larr; Back</button>
        <span style={st.subTitle}>{displayName(selectedAgent)}</span>
        <button style={st.addBtn} onClick={newSession} disabled={loading}>{loading ? '...' : '+ New'}</button>
      </div>
      <div style={st.scroll}>
        {sessions.length === 0 && <div style={{ ...st.center, paddingTop: 40 }}><div style={{ color: C.t3, fontSize: 13, marginBottom: 16 }}>No conversations yet</div><button style={st.btnRun} onClick={newSession}>Start conversation</button></div>}
        {sessions.map(session => (
          <div key={session.id} style={{ ...st.card, cursor: 'pointer' }} onClick={() => { setActiveSession(session); setMessages(session.messages || []); }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <div style={{ color: C.t1, fontSize: 14, fontWeight: 600 }}>{session.session_title || 'Conversation'}</div>
              <Badge status={session.status} />
            </div>
            <div style={{ color: C.t3, fontSize: 11, marginBottom: 6 }}>{(session.messages || []).length} messages · {new Date(session.updated_at).toLocaleDateString()}</div>
            {session.messages?.length > 0 && <div style={{ color: C.t2, fontSize: 12, lineHeight: 1.5 }}>{session.messages[session.messages.length - 1]?.content?.slice(0, 100)}</div>}
          </div>
        ))}
      </div>
    </div>
  );

  if (selectedBiz) return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div style={st.subHeader}>
        <button style={st.backBtn} onClick={() => setSelectedBiz(null)}>&larr; Back</button>
        <span style={st.subTitle}>{biz?.name}</span>
        <div style={{ width: 60 }} />
      </div>
      <div style={st.scroll}>
        {(biz?.agents || []).map((agent: string) => (
          <div key={agent} style={{ ...st.card, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }} onClick={() => { setSelectedAgent(agent); loadSessions(selectedBiz!, agent); }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 40, height: 40, borderRadius: 20, background: C.accentDim, border: `0.5px solid ${C.accentBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.accent, fontSize: 16, fontWeight: 700, flexShrink: 0 }}>{displayName(agent)[0].toUpperCase()}</div>
              <div><div style={{ color: C.t1, fontSize: 14, fontWeight: 600 }}>{displayName(agent)}</div><div style={{ color: C.t3, fontSize: 12, marginTop: 2 }}>Tap to chat</div></div>
            </div>
            <span style={{ color: C.accent, fontSize: 20 }}>&rsaquo;</span>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div style={st.scroll}>
      <div style={st.sectionTitle}>Select a business</div>
      {businesses.map(b => (
        <div key={b.key} style={{ ...st.card, cursor: 'pointer' }} onClick={() => setSelectedBiz(b.key)}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>{b.icon || '🏢'}</div>
          <div style={{ color: C.t1, fontSize: 16, fontWeight: 600 }}>{b.name}</div>
          <div style={{ color: C.t3, fontSize: 12, marginTop: 4 }}>{b.agents?.length || 1} team members</div>
        </div>
      ))}
    </div>
  );
}

function DocumentsTab() {
  const [documents, setDocuments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const data = await apiFetch('/documents'); setDocuments(data.documents || []); } catch(e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const typeIcon = (type: string) => type === 'pdf' ? '📄' : type === 'excel' ? '📊' : type === 'chart' ? '📈' : '📁';
  const typeColor = (type: string) => type === 'pdf' ? C.neg : type === 'excel' ? C.pos : type === 'chart' ? C.accent : C.t3;

  const download = async (filename: string) => {
    setDownloading(filename);
    try {
      const res = await fetch(`${API_URL}/documents/download?filename=${encodeURIComponent(filename)}`, { headers: { 'X-API-Key': API_KEY } });
      if (!res.ok) { alert('Download failed'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch(e) { console.error(e); }
    setDownloading(null);
  };

  if (loading) return <div style={st.center}><Spinner /></div>;

  return (
    <div style={st.scroll}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <div style={{ color: C.t3, fontSize: 12 }}>{documents.length} document{documents.length !== 1 ? 's' : ''} generated</div>
        <button onClick={load} style={{ background: 'none', border: 'none', color: C.accent, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>↻ Refresh</button>
      </div>
      {documents.length === 0 && (
        <div style={st.center}>
          <div style={{ fontSize: 32, marginBottom: 16 }}>📄</div>
          <div style={{ color: C.t1, fontSize: 16, marginBottom: 8 }}>No documents yet</div>
          <div style={{ color: C.t3, fontSize: 13, textAlign: 'center', maxWidth: 260 }}>Ask any agent to generate a PDF report, Excel spreadsheet, or chart and it will appear here.</div>
        </div>
      )}
      {documents.map(doc => (
        <div key={doc.filename} style={st.card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ fontSize: 28, flexShrink: 0 }}>{typeIcon(doc.type)}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: C.t1, fontSize: 14, fontWeight: 600, marginBottom: 3, wordBreak: 'break-all' }}>{doc.filename}</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ background: `${typeColor(doc.type)}22`, color: typeColor(doc.type), borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>{doc.type.toUpperCase()}</span>
                <span style={{ color: C.t3, fontSize: 11 }}>{doc.size_human}</span>
              </div>
            </div>
            <button onClick={() => download(doc.filename)} disabled={downloading === doc.filename} style={{ background: C.accentDim, border: `0.5px solid ${C.accentBorder}`, borderRadius: 8, padding: '8px 14px', color: C.accent, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 }}>{downloading === doc.filename ? '...' : '⬇ Download'}</button>
          </div>
        </div>
      ))}
      <div style={{ ...st.card, background: C.bg1, border: `0.5px dashed ${C.borderHi}`, marginTop: 8 }}>
        <div style={{ color: C.t3, fontSize: 12, lineHeight: 1.6 }}><strong style={{ color: C.t2 }}>Tip:</strong> Ask any agent to generate documents.</div>
        <div style={{ color: C.t3, fontSize: 11, marginTop: 8, lineHeight: 1.8 }}>• "Generate a PDF portfolio report"<br/>• "Create an Excel spreadsheet of my positions"<br/>• "Generate a bar chart of broker allocations"</div>
      </div>
    </div>
  );
}

function ChatTab() {
  const [messages, setMessages] = useState<any[]>([{ id: '0', role: 'agent', text: "Hello, I'm your Chief Operating Officer. How can I help?" }]);
  const [loading, setLoading] = useState(false);
  const [lastUserText, setLastUserText] = useState('');
  const endRef = useRef<HTMLDivElement>(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const send = async (text: string, image?: { base64: string; mime_type: string; preview: string }) => {
    if (!text && !image) return; if (loading) return;
    const userMsg: any = { id: Date.now().toString(), role: 'user', text: text || 'Image' };
    if (image) userMsg.image_preview = image.preview;
    setMessages(prev => [...prev.filter(m => m.id !== 'error'), userMsg]);
    setLastUserText(text); setLoading(true);
    try {
      const data = image ? await apiFetch('/chat-with-image', 'POST', { text: text || 'What do you see?', image_base64: image.base64, mime_type: image.mime_type }) : await apiFetch('/chat', 'POST', { text });
      setMessages(prev => [...prev, { id: (Date.now()+1).toString(), role: 'agent', text: data.response }]);
    } catch { setMessages(prev => [...prev, { id: 'error', role: 'error', text: 'Connection error. Check server status.' }]); }
    setLoading(false);
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {messages.map(msg => {
          if (msg.role === 'error') return (
            <div key={msg.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
              <div style={{ background: C.negDim, border: `0.5px solid ${C.negBorder}`, borderRadius: 12, padding: '10px 14px', color: C.neg, fontSize: 13 }}>{msg.text}</div>
              <button onClick={() => send(lastUserText)} style={{ background: C.accentDim, border: `0.5px solid ${C.accentBorder}`, borderRadius: 8, padding: '5px 12px', color: C.accent, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>↺ Retry</button>
            </div>
          );
          return (
            <div key={msg.id} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
              <div style={{ background: msg.role === 'user' ? C.accent : C.bg2, color: C.t1, borderRadius: 16, borderBottomRightRadius: msg.role === 'user' ? 4 : 16, borderBottomLeftRadius: msg.role === 'user' ? 16 : 4, padding: '10px 14px', maxWidth: '82%', border: msg.role === 'agent' ? `0.5px solid ${C.border}` : 'none' }}>
                {msg.image_preview && <img src={msg.image_preview} alt="attachment" style={{ maxWidth: '100%', borderRadius: 8, marginBottom: 6 }} />}
                {msg.role === 'agent' ? <Markdown text={msg.text} /> : <div style={{ fontSize: 14, lineHeight: 1.6 }}>{msg.text}</div>}
              </div>
            </div>
          );
        })}
        {loading && <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: C.t3, fontSize: 12 }}><Spinner />COO working...</div>}
        <div ref={endRef} />
      </div>
      <ChatInputBar onSend={send} placeholder="Message your COO..." disabled={loading} />
    </div>
  );
}

type Tab = 'dashboard' | 'proposals' | 'approvals' | 'tasks' | 'agents' | 'documents' | 'chat';

export default function App() {
  const [tab, setTab] = useState<Tab>(() => (localStorage.getItem('humble_ops_tab') as Tab) || 'dashboard');
  useEffect(() => { localStorage.setItem('humble_ops_tab', tab); }, [tab]);
  const [pendingCount, setPendingCount] = useState(0);
  const [approvalCount, setApprovalCount] = useState(0);

  useEffect(() => {
    const loadCount = async () => {
      try {
        const [m, a] = await Promise.all([apiFetch('/proposals'), apiFetch('/apex-proposals')]);
        setPendingCount((m.proposals||[]).filter((p:any) => p.status==='pending').length + (a.proposals||[]).filter((p:any) => p.status==='pending').length);
        try { const appr = await apiFetch('/approvals'); setApprovalCount((appr.approvals||[]).length); } catch {}
      } catch {}
    };
    loadCount();
    const t = setInterval(loadCount, 30000);
    return () => clearInterval(t);
  }, []);

  const tabs: { key: Tab; label: string }[] = [
    { key: 'dashboard', label: 'Dashboard' },
    { key: 'proposals', label: pendingCount > 0 ? `Proposals (${pendingCount})` : 'Proposals' },
    { key: 'approvals', label: approvalCount > 0 ? `Approvals (${approvalCount})` : 'Approvals' },
    { key: 'tasks', label: 'Tasks' },
    { key: 'agents', label: 'Agents' },
    { key: 'documents', label: 'Docs' },
    { key: 'chat', label: 'Chat' },
  ];

  return (
    <div style={{ position: 'fixed', inset: 0, background: C.bg0, color: C.t1, fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", sans-serif', display: 'flex', flexDirection: 'column', WebkitFontSmoothing: 'antialiased' } as React.CSSProperties}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', background: C.bg0, borderBottom: `0.5px solid ${C.border}`, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 36, height: 36, background: C.bg2, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `0.5px solid ${C.borderHi}`, position: 'relative', flexShrink: 0 }}>
            <span style={{ color: C.t1, fontSize: 20, fontWeight: 600, letterSpacing: -1, marginTop: -4 }}>H</span>
            <div style={{ position: 'absolute', bottom: 5, width: 20, height: 2, borderRadius: 1, background: C.accent }} />
          </div>
          <div>
            <div style={{ color: C.t1, fontSize: 16, fontWeight: 600 }}>Humble Ops</div>
            <div style={{ color: C.t3, fontSize: 11, marginTop: 1 }}>Operations Manager</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={registerPushNotifications} title="Enable notifications" style={{ background: C.accentDim, border: `0.5px solid ${C.accentBorder}`, borderRadius: 8, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 16 }}>🔔</button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{ width: 6, height: 6, borderRadius: 3, background: C.pos }} />
            <span style={{ color: C.pos, fontSize: 12, fontWeight: 600 }}>Live</span>
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', background: C.bg0, borderBottom: `0.5px solid ${C.border}`, flexShrink: 0 }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{ flex: 1, padding: '11px 4px', background: 'none', border: 'none', cursor: 'pointer', borderBottom: tab === t.key ? `2px solid ${C.accent}` : '2px solid transparent', color: tab === t.key ? C.accent : C.t3, fontSize: 12, fontWeight: tab === t.key ? 600 : 400, whiteSpace: 'nowrap', fontFamily: 'inherit', transition: 'color 0.15s' }}>{t.label}</button>
        ))}
      </div>
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {tab === 'dashboard' && <DashboardTab />}
        {tab === 'proposals' && <ProposalsTab />}
        {tab === 'approvals' && <ApprovalsTab />}
        {tab === 'tasks'     && <TasksTab />}
        {tab === 'agents'    && <AgentsTab />}
        {tab === 'documents' && <DocumentsTab />}
        {tab === 'chat'      && <ChatTab />}
      </div>
    </div>
  );
}

const st: Record<string, React.CSSProperties> = {
  scroll: { flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 10 },
  center: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 32 },
  spinner: { width: 18, height: 18, border: `2px solid ${C.bg3}`, borderTopColor: C.accent, borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 },
  card: { background: C.bg2, borderRadius: 14, padding: 16, border: `0.5px solid ${C.border}`, flexShrink: 0 },
  metricsRow: { display: 'flex', gap: 6, marginTop: 12 },
  metric: { flex: 1, background: C.bg1, borderRadius: 8, padding: '8px 4px', textAlign: 'center' },
  metricV: { color: '#F0F0F8', fontSize: 13, fontWeight: 600 },
  metricL: { color: '#55556A', fontSize: 10, marginTop: 2 },
  sectionTitle: { color: '#F0F0F8', fontSize: 18, fontWeight: 600, marginBottom: 4, flexShrink: 0 },
  subHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: `0.5px solid rgba(255,255,255,0.07)`, background: '#111118', flexShrink: 0 },
  subTitle: { color: '#F0F0F8', fontSize: 15, fontWeight: 600, flex: 1, textAlign: 'center' },
  backBtn: { background: 'none', border: 'none', color: '#5B8DEF', fontSize: 14, cursor: 'pointer', padding: 0, fontFamily: 'inherit', minWidth: 60 },
  addBtn: { background: 'none', border: 'none', color: '#5B8DEF', fontSize: 14, cursor: 'pointer', padding: 0, fontFamily: 'inherit', minWidth: 60, textAlign: 'right' },
  btnApprove: { flex: 1, background: 'rgba(52,199,89,0.12)', border: `0.5px solid rgba(52,199,89,0.35)`, borderRadius: 9, padding: '10px 0', color: '#34C759', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  btnReject: { flex: 1, background: 'rgba(255,59,48,0.1)', border: `0.5px solid rgba(255,59,48,0.3)`, borderRadius: 9, padding: '10px 0', color: '#FF3B30', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  btnInfo: { flex: 1, background: 'rgba(91,141,239,0.15)', border: `0.5px solid rgba(91,141,239,0.3)`, borderRadius: 9, padding: '10px 0', color: '#5B8DEF', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  btnRun: { background: 'rgba(91,141,239,0.15)', border: `0.5px solid rgba(91,141,239,0.3)`, borderRadius: 8, padding: '7px 14px', color: '#5B8DEF', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  btnCancel: { background: 'rgba(255,159,10,0.12)', border: `0.5px solid rgba(255,159,10,0.3)`, borderRadius: 8, padding: '7px 14px', color: '#FF9F0A', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  btnDel: { background: 'rgba(255,59,48,0.1)', border: `0.5px solid rgba(255,59,48,0.3)`, borderRadius: 8, padding: '7px 14px', color: '#FF3B30', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'flex-end', zIndex: 100 },
  modal: { background: '#16161F', borderRadius: '20px 20px 0 0', padding: 24, width: '100%', border: `0.5px solid rgba(255,255,255,0.07)`, maxHeight: '85vh', overflowY: 'auto' },
  modalTitle: { color: '#F0F0F8', fontSize: 18, fontWeight: 600, marginBottom: 20 },
  label: { color: '#55556A', fontSize: 11, fontWeight: 600, marginBottom: 6, letterSpacing: '0.05em' },
  input: { width: '100%', background: '#1C1C28', border: `0.5px solid rgba(255,255,255,0.07)`, borderRadius: 10, padding: '10px 12px', color: '#F0F0F8', fontSize: 14, marginBottom: 14, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' },
  chatInput: { flex: 1, background: '#16161F', border: `0.5px solid rgba(255,255,255,0.07)`, borderRadius: 18, padding: '9px 14px', color: '#F0F0F8', fontSize: 14, fontFamily: 'inherit', outline: 'none', resize: 'none', maxHeight: 100, overflowY: 'auto' },
  sendBtn: { background: '#5B8DEF', border: 'none', borderRadius: 18, padding: '9px 18px', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 },
};

(() => { const s = document.createElement('style'); s.textContent = '@keyframes spin { to { transform: rotate(360deg); } }'; document.head.appendChild(s); })();